import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Chip, IconButton, Tooltip,
  Divider, Tabs, Tab, Grid, Autocomplete, Switch, FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import CalculateIcon from '@mui/icons-material/Calculate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const API = '/api/importacion';

const ESTADOS_LABEL = {
  PENDIENTE: 'Pendiente', EN_TRANSITO: 'En Tránsito', EN_ADUANA: 'En Aduana',
  DESADUANADO: 'Desaduanado', LIQUIDADO: 'Liquidado'
};
const ESTADO_COLOR = {
  PENDIENTE: 'default', EN_TRANSITO: 'info', EN_ADUANA: 'warning',
  DESADUANADO: 'primary', LIQUIDADO: 'success'
};
const INCOTERMS = ['FOB', 'CIF', 'CFR', 'EXW', 'DAP', 'DDP'];
const TIPOS_GASTO = ['FLETE', 'SEGURO', 'ARANCEL', 'IGV_ADUANA', 'ALMACENAJE', 'AGENTE_ADUANA', 'PERCEPCION', 'OTROS'];
const METODOS_DIST = [
  { value: 'PROPORCIONAL_VALOR', label: 'Proporcional al Valor (USD)' },
  { value: 'PROPORCIONAL_PESO', label: 'Proporcional al Peso (kg)' },
  { value: 'PROPORCIONAL_CANTIDAD', label: 'Proporcional a Cantidad' },
];

function fmt(v, dec = 2) { return parseFloat(v || 0).toLocaleString('es-PE', { minimumFractionDigits: dec, maximumFractionDigits: dec }); }
function fmtMon(v) { return `S/ ${fmt(v)}`; }
function fmtUsd(v) { return `$ ${fmt(v)}`; }

const FORM_INIT = {
  proveedor_id: '', pais_origen: '', puerto_origen: '', puerto_destino: 'Callao',
  termino_comercial: 'FOB', bl_numero: '', contenedor_numero: '',
  fecha_embarque: '', fecha_llegada: '', moneda_compra: 'USD',
  tipo_cambio: '', metodo_distribucion: 'PROPORCIONAL_VALOR',
  observaciones: '', almacen_id: '', sucursal_id: ''
};
const ITEM_INIT = { producto_id: '', descripcion: '', cantidad: '', unidad_medida: 'NIU', peso_kg: '', precio_unitario_usd: '' };
const GASTO_INIT = { tipo_gasto: 'FLETE', descripcion: '', proveedor_gasto: '', numero_documento: '', monto: '', moneda: 'PEN', tipo_cambio: '1' };

export default function Importacion() {
  const [tab, setTab] = useState(0);
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [proveedores, setProveedores] = useState([]);
  const [productos, setProductos] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  // Dialog nuevo/editar
  const [dlgForm, setDlgForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_INIT);
  const [saving, setSaving] = useState(false);

  // Dialog detalle
  const [detalle, setDetalle] = useState(null);
  const [loadingDet, setLoadingDet] = useState(false);
  const [nuevoItem, setNuevoItem] = useState(ITEM_INIT);
  const [nuevoGasto, setNuevoGasto] = useState(GASTO_INIT);
  const [savingItem, setSavingItem] = useState(false);
  const [savingGasto, setSavingGasto] = useState(false);

  // Dialog distribución
  const [dlgDist, setDlgDist] = useState(null);
  const [loadingDist, setLoadingDist] = useState(false);
  const [liquidando, setLiquidando] = useState(false);

  const [busqProv, setBusqProv] = useState('');
  const [busqProd, setBusqProd] = useState('');

  // PDF dialog state
  const [openImpPdfDialog, setOpenImpPdfDialog] = useState(false);
  const [impPdfTarget, setImpPdfTarget] = useState(null);
  const [impPdfFormatoSeleccionado, setImpPdfFormatoSeleccionado] = useState(() => localStorage.getItem('importacion_pdf_formato_default') || 'A4');
  const [guardarImpPdfComoPredeterminado, setGuardarImpPdfComoPredeterminado] = useState(true);
  const [impPdfResolucion, setImpPdfResolucion] = useState({});
  const [impPdfResolucionCargando, setImpPdfResolucionCargando] = useState(false);

  const actualizarResolucionFormatoImp = async (nuevoFormato) => {
    if (!impPdfTarget) return;
    setImpPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-importacion/${impPdfTarget.id}`, { params: { formato: nuevoFormato } });
      setImpPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving IMP PDF template:', err);
    } finally {
      setImpPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdfImp = async (imp) => {
    const formatoGuardado = localStorage.getItem('importacion_pdf_formato_default') || 'A4';
    setImpPdfTarget(imp);
    setImpPdfFormatoSeleccionado(formatoGuardado);
    setGuardarImpPdfComoPredeterminado(true);
    setImpPdfResolucion({});
    setOpenImpPdfDialog(true);
    setImpPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-importacion/${imp.id}`, { params: { formato: formatoGuardado } });
      setImpPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving IMP PDF template:', err);
    } finally {
      setImpPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdfImp = () => {
    setOpenImpPdfDialog(false);
    setImpPdfTarget(null);
  };

  const descargarPdfImp = () => {
    if (!impPdfTarget) return;
    const formato = String(impPdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarImpPdfComoPredeterminado) {
      localStorage.setItem('importacion_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/importacion/${impPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdfImp();
  };

  const cargar = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(API); setLista(r.data); }
    catch { setError('Error al cargar importaciones'); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    axios.get('/api/auxiliares?tipo=PROVEEDOR').then(r => setProveedores(r.data)).catch(() => {});
    axios.get('/api/productos?limit=500').then(r => setProductos(r.data)).catch(() => {});
    axios.get('/api/almacen/mis-almacenes').then(r => setAlmacenes(r.data)).catch(() => {});
    axios.get('/api/sucursales/mis-sucursales').then(r => setSucursales(r.data)).catch(() => {});
  }, []);

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function guardar() {
    if (!form.proveedor_id) return setError('Proveedor requerido');
    setSaving(true); setError('');
    try {
      if (editando) {
        await axios.put(`${API}/${editando.id}`, form);
        setSuccess('Importación actualizada');
      } else {
        const r = await axios.post(API, form);
        setSuccess(`Expediente ${r.data.numero_expediente} creado`);
      }
      setDlgForm(false); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al guardar'); }
    setSaving(false);
  }

  async function abrirEditar(imp) {
    setEditando(imp);
    setForm({
      proveedor_id: imp.proveedor_id, pais_origen: imp.pais_origen || '',
      puerto_origen: imp.puerto_origen || '', puerto_destino: imp.puerto_destino || 'Callao',
      termino_comercial: imp.termino_comercial || 'FOB', bl_numero: imp.bl_numero || '',
      contenedor_numero: imp.contenedor_numero || '', fecha_embarque: imp.fecha_embarque?.slice(0, 10) || '',
      fecha_llegada: imp.fecha_llegada?.slice(0, 10) || '', moneda_compra: imp.moneda_compra || 'USD',
      tipo_cambio: imp.tipo_cambio || '', metodo_distribucion: imp.metodo_distribucion || 'PROPORCIONAL_VALOR',
      observaciones: imp.observaciones || '', almacen_id: imp.almacen_id || '', sucursal_id: imp.sucursal_id || '',
      estado: imp.estado
    });
    setDlgForm(true);
  }

  async function abrirDetalle(imp) {
    setLoadingDet(true); setDetalle({ id: imp.id, numero_expediente: imp.numero_expediente });
    try {
      const r = await axios.get(`${API}/${imp.id}`);
      setDetalle(r.data);
    } catch { setError('Error al cargar detalle'); }
    setLoadingDet(false);
  }

  async function agregarItem() {
    if (!nuevoItem.producto_id || !nuevoItem.cantidad) return setError('Producto y cantidad requeridos');
    setSavingItem(true);
    try {
      await axios.post(`${API}/${detalle.id}/items`, nuevoItem);
      setNuevoItem(ITEM_INIT);
      const r = await axios.get(`${API}/${detalle.id}`);
      setDetalle(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSavingItem(false);
  }

  async function eliminarItem(itemId) {
    try {
      await axios.delete(`${API}/${detalle.id}/items/${itemId}`);
      const r = await axios.get(`${API}/${detalle.id}`);
      setDetalle(r.data);
    } catch { setError('Error al eliminar item'); }
  }

  async function agregarGasto() {
    if (!nuevoGasto.tipo_gasto || !nuevoGasto.monto) return setError('Tipo y monto requeridos');
    setSavingGasto(true);
    try {
      await axios.post(`${API}/${detalle.id}/gastos`, nuevoGasto);
      setNuevoGasto(GASTO_INIT);
      const r = await axios.get(`${API}/${detalle.id}`);
      setDetalle(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
    setSavingGasto(false);
  }

  async function eliminarGasto(gastoId) {
    try {
      await axios.delete(`${API}/${detalle.id}/gastos/${gastoId}`);
      const r = await axios.get(`${API}/${detalle.id}`);
      setDetalle(r.data);
    } catch { setError('Error al eliminar gasto'); }
  }

  async function verDistribucion() {
    setLoadingDist(true); setDlgDist(null);
    try {
      const r = await axios.get(`${API}/${detalle.id}/distribucion`);
      setDlgDist(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular distribución'); }
    setLoadingDist(false);
  }

  async function liquidar() {
    if (!window.confirm('¿Liquidar esta importación? Se registrará el ingreso al kardex de todos los items con el costo real calculado.')) return;
    setLiquidando(true);
    try {
      await axios.post(`${API}/${detalle.id}/liquidar`);
      setSuccess('Importación liquidada y kardex actualizado');
      setDlgDist(null); setDetalle(null); cargar();
    } catch (e) { setError(e.response?.data?.error || 'Error al liquidar'); }
    setLiquidando(false);
  }

  const provOptions = proveedores.filter(p =>
    !busqProv || (p.razon_social || '').toLowerCase().includes(busqProv.toLowerCase()) ||
    (p.numero_documento || '').includes(busqProv)
  ).slice(0, 60);

  const prodOptions = productos.filter(p =>
    !busqProd || p.nombre.toLowerCase().includes(busqProd.toLowerCase()) ||
    (p.codigo || '').toLowerCase().includes(busqProd.toLowerCase())
  ).slice(0, 60);

  const esLiquidado = detalle?.estado === 'LIQUIDADO';

  return (
    <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto', mt: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <LocalShippingIcon color="primary" />
        <Typography variant="h5" fontWeight="bold">Importaciones</Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={cargar} disabled={loading}><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setEditando(null); setForm(FORM_INIT); setDlgForm(true); }}>
          Nueva Importación
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Resumen KPIs */}
      {lista.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total expedientes', value: lista.length },
            { label: 'En tránsito', value: lista.filter(i => i.estado === 'EN_TRANSITO').length, color: 'info.main' },
            { label: 'En aduana', value: lista.filter(i => i.estado === 'EN_ADUANA').length, color: 'warning.main' },
            { label: 'Liquidados', value: lista.filter(i => i.estado === 'LIQUIDADO').length, color: 'success.main' },
          ].map((k, i) => (
            <Grid key={i} item xs={6} sm={3}>
              <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                <Typography fontWeight="bold" color={k.color || 'text.primary'}>{k.value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      {loading ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} /> : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'primary.main' }}>
                {['Expediente', 'Proveedor', 'Incoterm', 'BL', 'F. Embarque', 'F. Llegada',
                  'Compra USD', 'Gastos PEN', 'Costo Total PEN', 'Estado', 'Acc.'].map(h => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lista.length === 0 ? (
                <TableRow><TableCell colSpan={11} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay importaciones registradas</TableCell></TableRow>
              ) : lista.map(imp => (
                <TableRow key={imp.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{imp.numero_expediente}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{imp.proveedor_nombre}</Typography>
                    <Typography variant="caption" color="text.secondary">{imp.proveedor_ruc}</Typography>
                  </TableCell>
                  <TableCell align="center"><Chip label={imp.termino_comercial} size="small" variant="outlined" /></TableCell>
                  <TableCell sx={{ fontSize: '0.8rem' }}>{imp.bl_numero || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{imp.fecha_embarque?.slice(0, 10) || '—'}</TableCell>
                  <TableCell sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{imp.fecha_llegada?.slice(0, 10) || '—'}</TableCell>
                  <TableCell align="right">{fmtUsd(imp.total_compra_usd)}</TableCell>
                  <TableCell align="right">{fmtMon(imp.total_gastos_pen)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtMon(imp.total_costo_pen)}</TableCell>
                  <TableCell align="center"><Chip label={ESTADOS_LABEL[imp.estado] || imp.estado} size="small" color={ESTADO_COLOR[imp.estado] || 'default'} /></TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Ver / Gestionar"><IconButton size="small" color="info" onClick={() => abrirDetalle(imp)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                    {imp.estado !== 'LIQUIDADO' && <Tooltip title="Editar"><IconButton size="small" onClick={() => abrirEditar(imp)}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                    <Tooltip title="PDF"><IconButton size="small" color="secondary" onClick={() => abrirDialogoPdfImp(imp)}><PictureAsPdfIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog Nuevo / Editar */}
      <Dialog open={dlgForm} onClose={() => setDlgForm(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {editando ? `Editar ${editando.numero_expediente}` : 'Nueva Importación'}
          <IconButton onClick={() => setDlgForm(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pt: 1 }}>
            <Autocomplete size="small" sx={{ flex: 1, minWidth: '22em' }}
              options={provOptions}
              getOptionLabel={p => `${p.numero_documento} — ${p.razon_social}`}
              value={proveedores.find(p => p.id === form.proveedor_id) || null}
              onChange={(_, v) => setF('proveedor_id', v?.id || '')}
              onInputChange={(_, v) => setBusqProv(v)}
              renderInput={params => <TextField {...params} label="Proveedor *" />}
            />
            <TextField select size="small" label="Incoterm" value={form.termino_comercial}
              onChange={e => setF('termino_comercial', e.target.value)} sx={{ minWidth: '9em' }}>
              {INCOTERMS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
            </TextField>
            {editando && (
              <TextField select size="small" label="Estado" value={form.estado || 'PENDIENTE'}
                onChange={e => setF('estado', e.target.value)} sx={{ minWidth: '12em' }}>
                {Object.entries(ESTADOS_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
            )}
            <TextField size="small" label="País origen" value={form.pais_origen}
              onChange={e => setF('pais_origen', e.target.value)} sx={{ minWidth: '11em' }} />
            <TextField size="small" label="Puerto origen" value={form.puerto_origen}
              onChange={e => setF('puerto_origen', e.target.value)} sx={{ minWidth: '11em' }} />
            <TextField size="small" label="Puerto destino" value={form.puerto_destino}
              onChange={e => setF('puerto_destino', e.target.value)} sx={{ minWidth: '11em' }} />
            <TextField size="small" label="N° BL" value={form.bl_numero}
              onChange={e => setF('bl_numero', e.target.value)} sx={{ minWidth: '13em' }} />
            <TextField size="small" label="N° Contenedor" value={form.contenedor_numero}
              onChange={e => setF('contenedor_numero', e.target.value)} sx={{ minWidth: '13em' }} />
            <TextField size="small" type="date" label="Fecha embarque" InputLabelProps={{ shrink: true }}
              value={form.fecha_embarque} onChange={e => setF('fecha_embarque', e.target.value)} sx={{ minWidth: '12em' }} />
            <TextField size="small" type="date" label="Fecha llegada" InputLabelProps={{ shrink: true }}
              value={form.fecha_llegada} onChange={e => setF('fecha_llegada', e.target.value)} sx={{ minWidth: '12em' }} />
            <TextField select size="small" label="Moneda compra" value={form.moneda_compra}
              onChange={e => setF('moneda_compra', e.target.value)} sx={{ minWidth: '10em' }}>
              <MenuItem value="USD">USD</MenuItem>
              <MenuItem value="EUR">EUR</MenuItem>
              <MenuItem value="PEN">PEN</MenuItem>
            </TextField>
            <TextField size="small" label="Tipo de cambio" type="number" value={form.tipo_cambio}
              onChange={e => setF('tipo_cambio', e.target.value)} sx={{ width: '10em' }} />
            <TextField select size="small" label="Método distribución gastos" value={form.metodo_distribucion}
              onChange={e => setF('metodo_distribucion', e.target.value)} sx={{ minWidth: '20em' }}>
              {METODOS_DIST.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Almacén destino" value={form.almacen_id}
              onChange={e => setF('almacen_id', e.target.value)} sx={{ minWidth: '14em' }}>
              <MenuItem value="">— Sin almacén —</MenuItem>
              {almacenes.map(a => <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Sucursal" value={form.sucursal_id}
              onChange={e => setF('sucursal_id', e.target.value)} sx={{ minWidth: '14em' }}>
              <MenuItem value="">— Sin sucursal —</MenuItem>
              {sucursales.map(s => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
            </TextField>
            <TextField size="small" fullWidth label="Observaciones" multiline rows={2}
              value={form.observaciones} onChange={e => setF('observaciones', e.target.value)} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={saving}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editando ? 'Actualizar' : 'Crear Expediente')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Detalle */}
      <Dialog open={!!detalle} onClose={() => setDetalle(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShippingIcon />
            <Typography fontWeight="bold">{detalle?.numero_expediente}</Typography>
            {detalle?.estado && <Chip label={ESTADOS_LABEL[detalle.estado] || detalle.estado} size="small" color={ESTADO_COLOR[detalle.estado] || 'default'} />}
          </Box>
          <IconButton onClick={() => setDetalle(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {loadingDet ? <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} /> : detalle?.proveedor_nombre && (
            <Box>
              {/* Info cabecera */}
              <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {[
                  ['Proveedor', `${detalle.proveedor_nombre} (${detalle.proveedor_ruc})`],
                  ['Incoterm', detalle.termino_comercial],
                  ['BL', detalle.bl_numero || '—'],
                  ['T/C', detalle.tipo_cambio],
                  ['F. Embarque', detalle.fecha_embarque?.slice(0, 10) || '—'],
                  ['F. Llegada', detalle.fecha_llegada?.slice(0, 10) || '—'],
                  ['Método dist.', METODOS_DIST.find(m => m.value === detalle.metodo_distribucion)?.label || ''],
                  ['Almacén', detalle.almacen_nombre || '—'],
                ].map(([label, val]) => (
                  <Grid key={label} item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={500}>{val}</Typography>
                  </Grid>
                ))}
              </Grid>

              {/* Totales */}
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Box><Typography variant="caption" color="text.secondary">Compra</Typography><Typography fontWeight={700}>{fmtUsd(detalle.total_compra_usd)} = {fmtMon(detalle.total_compra_pen)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Gastos</Typography><Typography fontWeight={700} color="warning.main">{fmtMon(detalle.total_gastos_pen)}</Typography></Box>
                <Box><Typography variant="caption" color="text.secondary">Costo Total</Typography><Typography fontWeight={700} color="primary.main">{fmtMon(detalle.total_costo_pen)}</Typography></Box>
              </Paper>

              <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5 }}>
                <Tab label={`Items (${(detalle.items || []).length})`} />
                <Tab label={`Gastos (${(detalle.gastos || []).length})`} />
              </Tabs>

              {/* TAB ITEMS */}
              {tab === 0 && (
                <Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          {['Producto', 'Cantidad', 'Unidad', 'Peso kg', 'Precio USD', 'Total USD', 'Total PEN', esLiquidado && 'Costo Unit.', !esLiquidado && ''].filter(Boolean).map(h => (
                            <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detalle.items || []).length === 0 ? (
                          <TableRow><TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>Sin items</TableCell></TableRow>
                        ) : (detalle.items || []).map(it => (
                          <TableRow key={it.id} hover>
                            <TableCell>
                              <Typography variant="body2">{it.producto_nombre}</Typography>
                              <Typography variant="caption" color="text.secondary">{it.producto_codigo}</Typography>
                            </TableCell>
                            <TableCell align="right">{fmt(it.cantidad)}</TableCell>
                            <TableCell>{it.unidad_medida}</TableCell>
                            <TableCell align="right">{it.peso_kg ? fmt(it.peso_kg) : '—'}</TableCell>
                            <TableCell align="right">{fmtUsd(it.precio_unitario_usd)}</TableCell>
                            <TableCell align="right">{fmtUsd(it.total_usd)}</TableCell>
                            <TableCell align="right">{fmtMon(it.total_pen)}</TableCell>
                            {esLiquidado && <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{fmtMon(it.costo_unitario_pen)}</TableCell>}
                            {!esLiquidado && (
                              <TableCell align="center">
                                <IconButton size="small" color="error" onClick={() => eliminarItem(it.id)}><DeleteIcon fontSize="small" /></IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!esLiquidado && (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>Agregar Item</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <Autocomplete size="small" sx={{ flex: 1, minWidth: '18em' }}
                          options={prodOptions}
                          getOptionLabel={p => `${p.codigo ? p.codigo + ' - ' : ''}${p.nombre}`}
                          value={productos.find(p => p.id === nuevoItem.producto_id) || null}
                          onChange={(_, v) => setNuevoItem(i => ({ ...i, producto_id: v?.id || '' }))}
                          onInputChange={(_, v) => setBusqProd(v)}
                          renderInput={params => <TextField {...params} label="Producto *" />}
                        />
                        <TextField size="small" label="Cantidad *" type="number" value={nuevoItem.cantidad}
                          onChange={e => setNuevoItem(i => ({ ...i, cantidad: e.target.value }))} sx={{ width: '8em' }} />
                        <TextField size="small" label="Unidad" value={nuevoItem.unidad_medida}
                          onChange={e => setNuevoItem(i => ({ ...i, unidad_medida: e.target.value }))} sx={{ width: '7em' }} />
                        <TextField size="small" label="Peso kg" type="number" value={nuevoItem.peso_kg}
                          onChange={e => setNuevoItem(i => ({ ...i, peso_kg: e.target.value }))} sx={{ width: '8em' }} />
                        <TextField size="small" label="Precio USD *" type="number" value={nuevoItem.precio_unitario_usd}
                          onChange={e => setNuevoItem(i => ({ ...i, precio_unitario_usd: e.target.value }))} sx={{ width: '9em' }} />
                        <TextField size="small" label="Descripción" value={nuevoItem.descripcion}
                          onChange={e => setNuevoItem(i => ({ ...i, descripcion: e.target.value }))} sx={{ minWidth: '12em' }} />
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={agregarItem} disabled={savingItem}>
                          {savingItem ? <CircularProgress size={16} /> : 'Agregar'}
                        </Button>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}

              {/* TAB GASTOS */}
              {tab === 1 && (
                <Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          {['Tipo', 'Descripción', 'Proveedor', 'N° Doc.', 'Monto', 'Moneda', 'Monto PEN', ''].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(detalle.gastos || []).length === 0 ? (
                          <TableRow><TableCell colSpan={8} align="center" sx={{ py: 2, color: 'text.secondary' }}>Sin gastos</TableCell></TableRow>
                        ) : (detalle.gastos || []).map(g => (
                          <TableRow key={g.id} hover>
                            <TableCell><Chip label={g.tipo_gasto} size="small" /></TableCell>
                            <TableCell>{g.descripcion || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{g.proveedor_gasto || '—'}</TableCell>
                            <TableCell sx={{ fontSize: '0.8rem' }}>{g.numero_documento || '—'}</TableCell>
                            <TableCell align="right">{fmt(g.monto)}</TableCell>
                            <TableCell>{g.moneda}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtMon(g.monto_pen)}</TableCell>
                            <TableCell align="center">
                              {!esLiquidado && <IconButton size="small" color="error" onClick={() => eliminarGasto(g.id)}><DeleteIcon fontSize="small" /></IconButton>}
                            </TableCell>
                          </TableRow>
                        ))}
                        {(detalle.gastos || []).length > 0 && (
                          <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell colSpan={6} align="right" sx={{ fontWeight: 'bold' }}>Total gastos PEN:</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', color: 'warning.main' }}>{fmtMon(detalle.total_gastos_pen)}</TableCell>
                            <TableCell />
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!esLiquidado && (
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Typography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>Agregar Gasto</Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <TextField select size="small" label="Tipo *" value={nuevoGasto.tipo_gasto}
                          onChange={e => setNuevoGasto(g => ({ ...g, tipo_gasto: e.target.value }))} sx={{ minWidth: '12em' }}>
                          {TIPOS_GASTO.map(t => <MenuItem key={t} value={t}>{t.replace('_', ' ')}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label="Descripción" value={nuevoGasto.descripcion}
                          onChange={e => setNuevoGasto(g => ({ ...g, descripcion: e.target.value }))} sx={{ minWidth: '12em' }} />
                        <TextField size="small" label="Proveedor gasto" value={nuevoGasto.proveedor_gasto}
                          onChange={e => setNuevoGasto(g => ({ ...g, proveedor_gasto: e.target.value }))} sx={{ minWidth: '12em' }} />
                        <TextField size="small" label="N° Doc." value={nuevoGasto.numero_documento}
                          onChange={e => setNuevoGasto(g => ({ ...g, numero_documento: e.target.value }))} sx={{ width: '9em' }} />
                        <TextField size="small" label="Monto *" type="number" value={nuevoGasto.monto}
                          onChange={e => setNuevoGasto(g => ({ ...g, monto: e.target.value }))} sx={{ width: '9em' }} />
                        <TextField select size="small" label="Moneda" value={nuevoGasto.moneda}
                          onChange={e => setNuevoGasto(g => ({ ...g, moneda: e.target.value }))} sx={{ width: '7em' }}>
                          <MenuItem value="PEN">PEN</MenuItem>
                          <MenuItem value="USD">USD</MenuItem>
                        </TextField>
                        <TextField size="small" label="T/C" type="number" value={nuevoGasto.tipo_cambio}
                          onChange={e => setNuevoGasto(g => ({ ...g, tipo_cambio: e.target.value }))} sx={{ width: '7em' }} />
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={agregarGasto} disabled={savingGasto}>
                          {savingGasto ? <CircularProgress size={16} /> : 'Agregar'}
                        </Button>
                      </Box>
                    </Paper>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDetalle(null)}>Cerrar</Button>
          {!esLiquidado && (detalle?.items || []).length > 0 && (
            <Button variant="outlined" color="info" startIcon={<CalculateIcon />}
              onClick={verDistribucion} disabled={loadingDist}>
              {loadingDist ? <CircularProgress size={18} color="inherit" /> : 'Vista Distribución'}
            </Button>
          )}
          {!esLiquidado && (detalle?.items || []).length > 0 && (
            <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
              onClick={() => { verDistribucion(); }} disabled={loadingDist}>
              Ver y Liquidar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Dialog Distribución */}
      <Dialog open={!!dlgDist} onClose={() => setDlgDist(null)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Distribución de Costos — {detalle?.numero_expediente}
          <IconButton onClick={() => setDlgDist(null)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {dlgDist && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                  { label: 'Costo compra PEN', value: fmtMon(dlgDist.total_compra_pen), color: 'text.primary' },
                  { label: 'Total gastos PEN', value: fmtMon(dlgDist.total_gastos_pen), color: 'warning.main' },
                  { label: 'Costo total PEN', value: fmtMon(dlgDist.total_costo_pen), color: 'primary.main' },
                  { label: 'Método', value: METODOS_DIST.find(m => m.value === dlgDist.metodo)?.label || dlgDist.metodo, color: 'text.secondary' },
                ].map((k, i) => (
                  <Grid key={i} item xs={6} sm={3}>
                    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                      <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                      <Typography fontWeight="bold" color={k.color}>{k.value}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      {['Producto', 'Cantidad', 'Valor Compra PEN', 'Gastos Asignados', 'Costo Total PEN', 'Costo Unitario PEN'].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(dlgDist.items || []).map((it, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Typography variant="body2">{it.producto_nombre}</Typography>
                          <Typography variant="caption" color="text.secondary">{it.producto_codigo}</Typography>
                        </TableCell>
                        <TableCell align="right">{fmt(it.cantidad)}</TableCell>
                        <TableCell align="right">{fmtMon(it.total_pen)}</TableCell>
                        <TableCell align="right" sx={{ color: 'warning.main', fontWeight: 500 }}>{fmtMon(it.gastos_asignados_pen)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmtMon(it.costo_total_pen)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{fmtMon(it.costo_unitario_pen)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Alert severity="info" sx={{ mt: 2 }}>
                Al liquidar se actualizará el costo unitario de cada item y se registrará el ingreso al kardex del almacén seleccionado.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDlgDist(null)}>Cancelar</Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
            onClick={liquidar} disabled={liquidando}>
            {liquidando ? <CircularProgress size={20} color="inherit" /> : 'Confirmar Liquidación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Format Dialog - Importación */}
      <Dialog open={openImpPdfDialog} onClose={cerrarDialogoPdfImp} maxWidth="sm" fullWidth>
        <DialogTitle>Descargar PDF - Importación</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Selecciona el formato de impresión. El formato elegido puede quedar como predeterminado.
          </Alert>
          {impPdfResolucionCargando ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Resolviendo plantilla...</Typography>
            </Box>
          ) : impPdfResolucion && impPdfResolucion.plantilla_nombre ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Plantilla:</strong> {impPdfResolucion.plantilla_nombre}</Typography>
              <Typography variant="body2"><strong>Ámbito:</strong> {impPdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : impPdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : impPdfResolucion.scope}</Typography>
              {impPdfResolucion.is_inherited && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                  Esta sucursal hereda la asignación de la configuración global
                </Typography>
              )}
            </Alert>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
              { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
              { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
            ].map((item) => (
              <Paper
                key={item.codigo}
                onClick={() => {
                  setImpPdfFormatoSeleccionado(item.codigo);
                  actualizarResolucionFormatoImp(item.codigo);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: impPdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                  backgroundColor: impPdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
                {localStorage.getItem('importacion_pdf_formato_default') === item.codigo && (
                  <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
                )}
              </Paper>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={guardarImpPdfComoPredeterminado}
                onChange={(e) => setGuardarImpPdfComoPredeterminado(e.target.checked)}
                color="primary"
              />
            }
            label={`Guardar ${impPdfFormatoSeleccionado} como formato predeterminado`}
          />
          {impPdfTarget && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Documento: {impPdfTarget.numero_expediente || `IMP-${impPdfTarget.id}`}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoPdfImp}>Cancelar</Button>
          <Button onClick={descargarPdfImp} variant="contained">
            Descargar {impPdfFormatoSeleccionado}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
